/**
 * 3D シーンの構築（レンダラ側）。
 *
 * 軽量な手続き型メッシュを保ちながら、PBR 素材・影・室内照明で奥行きを出す。
 * メッシュの組み立て自体は sceneGraph.ts が受け持つ（レンダラに依存しないため、
 * テストから本番と同じ形状を組み立てて「狙えるか」を検査できる）。
 */

import * as THREE from 'three';
import type { NetworkWorld } from '../types/network';
import type { Interactable } from '../types/scenario';
import type { OfficeLayout } from './layout';
import { OfficeImageMaterials } from './officeDetails';
import { FurnitureModels } from './assets/furnitureModels';
import { ModelLibrary } from './assets/modelLibrary';
import { CharacterModels } from './assets/characterModels';
import { PerformancePanel } from './performance';
import { LocalLightPool } from './localLights';
import {
  buildInteractableMeshes,
  buildOfficeMeshes,
  disposeGroup,
  refreshLinkLeds,
  type InteractableHandle,
} from './sceneGraph';

export type { InteractableHandle } from './sceneGraph';

export class OfficeScene {
  private readonly localLights = new LocalLightPool();
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly renderer: THREE.WebGLRenderer;

  private interactableHandles: InteractableHandle[] = [];
  private highlighted: THREE.Object3D | null = null;
  private ledsByLink = new Map<string, THREE.MeshStandardMaterial[]>();
  private staticRoot = new THREE.Group();
  private propRoot = new THREE.Group();
  private readonly sun: THREE.DirectionalLight;
  private readonly performancePanel: PerformancePanel;
  private imageMaterials = new OfficeImageMaterials();
  private readonly modelStatus = document.createElement('button');
  private readonly characters = new CharacterModels(new ModelLibrary(), () => {
    if (this.highlighted) this.applyEmissive(this.highlighted, true);
  });
  private readonly characterKey = (event: KeyboardEvent): void => {
    if (event.key === 'F7' && !event.repeat) { event.preventDefault(); this.characters.toggle(); }
  };
  private readonly furniture = new FurnitureModels(new ModelLibrary(), status => {
    this.modelStatus.hidden = status.failed === 0;
    this.modelStatus.textContent = status.failed > 0
      ? '家具モデルを読み込めませんでした。簡易表示で続行中（クリックで再試行）' : '';
  }, () => {
    if (this.highlighted) this.applyEmissive(this.highlighted, true);
  });

  constructor(private readonly canvas: HTMLCanvasElement) {
    window.addEventListener('keydown', this.characterKey);
    this.modelStatus.className = 'model-load-status';
    this.modelStatus.hidden = true;
    this.modelStatus.setAttribute('aria-live', 'polite');
    this.modelStatus.addEventListener('click', () => this.furniture.retry());
    document.body.append(this.modelStatus);
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    this.performancePanel = new PerformancePanel(this.renderer, () => {
      const lod = this.furniture.detailCounts;
      return [`LOD high / low: ${lod.high} / ${lod.low}`, `KTX2 loaded: ${this.scene.userData.compressedTextures ?? 0}`, 'Local lights: 8 max', this.characters.status];
    });
    const touchMode = navigator.maxTouchPoints > 0 || window.matchMedia?.('(pointer: coarse)').matches === true;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, touchMode ? 1 : 1.5));
    this.renderer.setSize(window.innerWidth, window.innerHeight, false);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 0.92;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    this.scene.background = new THREE.Color(0xc8d2d8);
    this.scene.fog = new THREE.Fog(0xc8d2d8, 58, 125);

    this.camera = new THREE.PerspectiveCamera(75, 1, 0.1, 200);
    this.camera.position.set(0, 1.65, 0);

    this.scene.add(new THREE.AmbientLight(0xfff9ee, 0.38));
    const hemi = new THREE.HemisphereLight(0xddeeff, 0x5c554c, 0.64);
    hemi.position.set(0, 20, 0);
    this.scene.add(hemi);
    this.sun = new THREE.DirectionalLight(0xf6f3eb, 1.15);
    this.sun.position.set(24, 34, 16);
    this.sun.castShadow = true;
    const shadowSize = touchMode ? 1024 : 2048;
    this.sun.shadow.mapSize.set(shadowSize, shadowSize);
    this.sun.shadow.camera.near = 1;
    this.sun.shadow.camera.far = 150;
    this.sun.shadow.bias = -0.00018;
    this.sun.shadow.normalBias = 0.035;
    this.scene.add(this.sun, this.sun.target);

    this.scene.add(this.staticRoot);
    this.scene.add(this.propRoot);
    this.scene.add(this.localLights.group);
    this.resize();
  }

  resize(): void {
    const width = this.canvas.clientWidth || window.innerWidth;
    const height = this.canvas.clientHeight || window.innerHeight;
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / Math.max(1, height);
    this.camera.updateProjectionMatrix();
  }

  setFov(fov: number): void {
    this.camera.fov = fov;
    this.camera.updateProjectionMatrix();
  }

  render(): void {
    this.characters.update(this.camera, performance.now());
    this.furniture.update(this.camera);
    this.localLights.update(this.camera);
    this.renderer.render(this.scene, this.camera);
    this.performancePanel.update(performance.now());
  }

  // ------------------------------------------------------------
  // 建物
  // ------------------------------------------------------------

  buildOffice(layout: OfficeLayout): void {
    this.scene.remove(this.staticRoot);
    disposeGroup(this.staticRoot);
    this.staticRoot = buildOfficeMeshes(layout);
    this.scene.add(this.staticRoot);
    this.localLights.bind(this.staticRoot);
    this.fitSunShadowTo(this.staticRoot);
  }

  // ------------------------------------------------------------
  // 調査対象オブジェクト
  // ------------------------------------------------------------

  buildInteractables(
    layout: OfficeLayout,
    interactables: Interactable[],
    world: NetworkWorld,
  ): void {
    this.characters.clear();
    this.furniture.clear();
    this.scene.remove(this.propRoot);
    disposeGroup(this.propRoot);
    this.highlighted = null;

    const built = buildInteractableMeshes(layout, interactables, world);
    this.propRoot = built.root;
    this.interactableHandles = built.handles;
    this.ledsByLink = built.ledsByLink;
    this.scene.add(this.propRoot);

    this.refreshLinkLeds(world);
    this.imageMaterials.dispose();
    this.imageMaterials = new OfficeImageMaterials();
    this.scene.userData.compressedTextures = 0;
    this.imageMaterials.apply(this.scene, this.renderer);
    this.furniture.attach(this.propRoot);
    void this.characters.attach(this.propRoot);
  }

  setDialogueSpeaker(speaker: string | null, line: string): void {
    this.characters.dialogue(speaker, line);
  }

  faceCharacter(interactableId: string): boolean {
    return this.characters.facePlayer(interactableId, this.camera);
  }

  /** リンク状態の変化を LED の色に反映する */
  refreshLinkLeds(world: NetworkWorld): void {
    refreshLinkLeds(this.ledsByLink, world);
  }

  // ------------------------------------------------------------
  // レイキャストとハイライト
  // ------------------------------------------------------------

  get handles(): InteractableHandle[] {
    return this.interactableHandles;
  }

  setHighlighted(id: string | null): void {
    if (this.highlighted) {
      this.applyEmissive(this.highlighted, false);
      this.highlighted = null;
    }
    if (!id) return;
    const handle = this.interactableHandles.find((h) => h.id === id);
    if (!handle) return;
    this.highlighted = handle.object;
    this.applyEmissive(handle.object, true);
  }

  private applyEmissive(root: THREE.Object3D, on: boolean): void {
    root.traverse((child) => {
      const mesh = child as THREE.Mesh;
      const material = mesh.material as THREE.MeshStandardMaterial | undefined;
      if (!material || !material.emissive) return;
      // LED は独自に色を持っているので触らない
      if (material.userData.statusLed === true) return;
      if (on) {
        material.userData.baseEmissive ??= material.emissive.getHex();
        material.userData.baseEmissiveIntensity ??= material.emissiveIntensity;
        material.emissive.setHex(0x3a4a2a);
        material.emissiveIntensity = 0.55;
      } else {
        const base = material.userData.baseEmissive;
        material.emissive.setHex(typeof base === 'number' ? base : 0x000000);
        const intensity = material.userData.baseEmissiveIntensity;
        material.emissiveIntensity = typeof intensity === 'number' ? intensity : 1;
      }
    });
  }

  dispose(): void {
    window.removeEventListener('keydown', this.characterKey);
    this.characters.dispose();
    this.performancePanel.dispose();
    this.furniture.dispose();
    this.modelStatus.remove();
    this.imageMaterials.dispose();
    disposeGroup(this.staticRoot);
    disposeGroup(this.propRoot);
    this.renderer.dispose();
    void this.canvas;
  }

  /** 建物全体を影の範囲に収め、解像度を空間の外へ浪費しない。 */
  private fitSunShadowTo(root: THREE.Object3D): void {
    root.updateMatrixWorld(true);
    const bounds = new THREE.Box3().setFromObject(root);
    if (bounds.isEmpty()) return;
    const center = bounds.getCenter(new THREE.Vector3());
    const size = bounds.getSize(new THREE.Vector3());
    const radius = Math.max(size.x, size.z) * 0.58 + 4;
    const camera = this.sun.shadow.camera;
    camera.left = -radius;
    camera.right = radius;
    camera.top = radius;
    camera.bottom = -radius;
    camera.updateProjectionMatrix();
    this.sun.target.position.copy(center);
    this.sun.position.set(center.x + 24, bounds.max.y + 28, center.z + 16);
    this.sun.target.updateMatrixWorld();
  }
}
